<template>
  <v-app>

    <PullToRefresh @refresh="handleGlobalRefresh" :disabled="isRefreshDisabled">
    <GameDayBanner v-if="userStore.isAuthenticated && ['/', '/teams', '/leagues'].includes(route.path)" ref="gameDayBannerRef" />
    <!-- Sticky container pro menu, které zůstane nahoře po odscrollování GameDayBanner -->
    <div ref="menuContainer" class="sticky-menu-container" :class="{ 'is-fixed': isMenuFixed }">
      <MainMenu v-if="!hideLayout && !hideUserBanner" />
      <MainMenuLeague v-if="seasonData?._id" :season="seasonData" />
      <MainMenuMatch v-else-if="isMatchRoute" />
      <!-- Team menu pro stránky týmů -->
      <MainMenuTeam 
        v-if="teamData?._id" 
        :teamData="teamData"
      />
      <!-- nic dalšího pro MainMenuMatch, aby se neukazovalo jinde -->
      <MainMenuStats v-if="seasonData?._id && !seasonData?.isAcademy && !route.path.startsWith('/gamecenter/')" :season="seasonData" />
    </div>
    <!-- Placeholder pro zachování výšky když je menu fixed -->
    <div v-if="isMenuFixed" :style="{ height: menuHeight + 'px' }"></div>

    <!-- Mobile: unified tab view for /, /teams, /leagues (no full route changes) -->
    <MobileHomeView v-if="isMobile" v-show="isMainRoute" style="padding-bottom:100px;" />

    <!-- Desktop, or non-main routes on mobile: regular router-view -->
    <router-view v-if="!isMobile || !isMainRoute" v-slot="{ Component, route: viewRoute }">
      <div class="rt-view-wrapper">
        <transition :name="routeTransition">
          <component :is="Component" :key="mainRouteKey(viewRoute.path)" @season-data="handleSeasonData" @team-data="handleTeamData" style="padding-bottom:100px;" />
        </transition>
      </div>
    </router-view>
    <!--<SessionExpiredDialog />-->
    <PlayOverlay :show="appStore.isPlayRequestVisible" @close="appStore.closePlayRequest" @login="handlePlayClose" />
    <!-- ✨ NEW: LoadingOverlay napojený na store -->
    <LoadingOverlay :show="appStore.isLoadingVisible" :text="appStore.loadingMessage || $t('loading')" />
    <Footer-modernhockey v-if="!hideLayout && BRAND === 'modernhockey'" />
    <!-- <Footer-hockeybook v-if="!hideLayout && BRAND === 'hockeybook'" />-->
    </PullToRefresh>
    <!-- MobileBottomNav musí být mimo PullToRefresh aby zůstala fixed na spodní hraně -->
    <MobileBottomNav />
    <!-- Fade overlay na spodním okraji mobilního zobrazení -->
    <div v-if="isMobile" class="mobile-bottom-fade" />
    <!-- Fade overlay na horním okraji mobilního zobrazení (jen při scrollu) -->
    <div v-if="isMobile && isScrolled" class="mobile-top-fade" />
    <!-- Global QuickActionsMenu — config driven from quickActionsStore -->
    <QuickActionsMenu />
    <!-- Minimized chat bubbles -->
    <MinimizedChats @restore="restoreMinimizedChat" />
    <!-- Phone-number prompt (shown after event registration when user has no phone) -->
    <PhonePromptBottomSheet />
  </v-app>
</template>


<script lang="ts" setup>
import { onMounted, onBeforeUnmount, computed, ref, watch, nextTick } from 'vue';
import { useAppStore } from './stores/app/appStore';
import { useMinimizedChatsStore } from '@/stores/chat/minimizedChatsStore';
import { useUserStore } from '@/stores/user/userStore';
import { useRoute, useRouter } from 'vue-router';
import SessionExpiredDialog from '@/components/SessionExpiredDialog.vue';
import PlayOverlay from '@/components/PlayOverlay.vue';
import MainMenuStats from './components/MainMenuStats.vue';
import MainMenuLeague from './components/MainMenuLeague.vue';
import MainMenuMatch from './components/MainMenuMatch.vue';
import MainMenuTeam from './components/MainMenuTeam.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import MobileBottomNav from '@/components/MobileBottomNav.vue';
import MobileHomeView from '@/components/MobileHomeView.vue';
import QuickActionsMenu from '@/components/QuickActionsMenu.vue';
import MinimizedChats from '@/components/chat/MinimizedChats.vue';
import PhonePromptBottomSheet from '@/components/PhonePromptBottomSheet.vue';
import FooterModernHockey from '@/components/Footer-modernhockey.vue';
import FooterHockeybook from '@/components/Footer-hockeybook.vue';
import GameDayBanner from './components/GameDayBanner.vue';
import PullToRefresh from './components/PullToRefresh.vue';
import { useDisplay, useTheme } from 'vuetify';
import { useNavTransition } from '@/composables/useNavTransition';

const display = useDisplay();                // ✅ nepoužívej destrukturing
const theme = useTheme();
const BRAND = import.meta.env.VITE_BRAND as string;

import { setStatusBarStyle } from '@/capacitor'

// Sync Vuetify theme to <html> data-theme attribute so global CSS (body, decorations) can react
const syncThemeToHtml = () => {
  const isDark = theme.global.current.value.dark;
  document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  document.body.style.backgroundColor = isDark ? '#060810' : '#f5f7fa';
  setStatusBarStyle(isDark);
};
watch(() => theme.global.name.value, syncThemeToHtml, { immediate: true });

const appStore = useAppStore();
const minimizedChatsStore = useMinimizedChatsStore();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();

function restoreMinimizedChat(conversationId: string) {
  minimizedChatsStore.restore(conversationId);
  router.push(`/messages/${conversationId}`);
}

// ─── Mobile tab navigation ─────────────────────────────────────────────
const { isMainRoute: isMainRouteFn } = useNavTransition();
const isMobile = computed(() => display.mobile.value);
const isMainRoute = computed(() => isMainRouteFn(route.path));

// ─── Desktop route slide transition ────────────────────────────────────
const MAIN_TAB_INDEX: Record<string, number> = { '/': 0, '/teams': 1, '/leagues': 2, '/messages': 3 };
const routeTransition = ref('');
let prevMainIndex = MAIN_TAB_INDEX[route.path] ?? -1;

/** Keep the same key for all non-main routes so only main↔main transitions trigger animation.
 *  Exception: eventdatalog routes get unique keys so prev/next navigation remounts the component. */
function mainRouteKey(path: string): string {
  if (path in MAIN_TAB_INDEX) return path;
  if (path.startsWith('/eventdatalog/')) return path;
  return '__detail__';
}

watch(() => route.path, (newPath, oldPath) => {
  const newIdx = MAIN_TAB_INDEX[newPath] ?? -1;
  const oldIdx = MAIN_TAB_INDEX[oldPath] ?? -1;
  if (newIdx >= 0 && oldIdx >= 0 && newIdx !== oldIdx) {
    routeTransition.value = newIdx > oldIdx ? 'rt-slide-left' : 'rt-slide-right';
  } else {
    routeTransition.value = '';
  }
  prevMainIndex = newIdx >= 0 ? newIdx : prevMainIndex;
});

const superliga = ref(route.query.superliga || null);
const seasonData = ref<any>(null);
const teamData = ref<any>(null);

// === Sticky menu logika ===
const menuContainer = ref<HTMLElement | null>(null);
const gameDayBannerRef = ref<any>(null);
const isMenuFixed = ref(false);
const menuHeight = ref(0);
const isScrolled = ref(false);

// Zjistí, zda se má banner vůbec zobrazovat na aktuální route
const isBannerRoute = computed(() => {
  return userStore.isAuthenticated && ['/', '/teams', '/leagues'].includes(route.path);
});

function getBannerHeight(): number {
  if (!isBannerRoute.value) return 0;
  const el = gameDayBannerRef.value?.$el as HTMLElement | undefined;
  return el?.offsetHeight || 0;
}

function handleScroll() {
  const scrollY = window.scrollY;
  isScrolled.value = scrollY > 30;
  const bannerH = getBannerHeight();

  // Pokud banner není na routě nebo ještě nemá výšku (data se načítají),
  // menu by mělo být vždy v normální pozici (ne fixed)
  if (!isBannerRoute.value || bannerH === 0) {
    isMenuFixed.value = false;
    return;
  }

  // Když se odscrolluje banner, menu se zafixuje
  if (scrollY >= bannerH) {
    if (!isMenuFixed.value && menuContainer.value) {
      menuHeight.value = menuContainer.value.offsetHeight;
    }
    isMenuFixed.value = true;
  } else {
    isMenuFixed.value = false;
  }
}

// Reset sticky stavu při změně route
watch(() => route.path, (newPath) => {
  isMenuFixed.value = false;
  menuHeight.value = 0;
  // Clear team/season data when leaving those routes
  if (!newPath.startsWith('/teams/')) teamData.value = null;
  if (!newPath.startsWith('/league') && !newPath.startsWith('/gamecenter/')) seasonData.value = null;
  // Přepočítat po renderování nové stránky
  nextTick(() => handleScroll());
});

const isMatchRoute = computed(() => route.path.startsWith('/match'));  // ✅ startsWith

const handlePlayClose = () => appStore.closePlayRequest();
const handleSeasonData = (data: any) => (seasonData.value = data);
const handleTeamData = (data: any) => (teamData.value = data);

// === League color live update (from LeagueFormModal in QuickActionsMenu) ===
const onLeagueColorsUpdated = (e: Event) => {
  const { primaryColor, secondaryColor } = (e as CustomEvent).detail
  if (seasonData.value) {
    seasonData.value = {
      ...seasonData.value,
      primaryColor: primaryColor ?? seasonData.value.primaryColor,
      secondaryColor: secondaryColor ?? seasonData.value.secondaryColor,
    }
  }
}
window.addEventListener('league-colors-updated', onLeagueColorsUpdated)

const onTeamColorsUpdated = (e: Event) => {
  const { primaryTeamColor, secondaryTeamColor } = (e as CustomEvent).detail
  if (teamData.value) {
    teamData.value = {
      ...teamData.value,
      primaryTeamColor: primaryTeamColor ?? teamData.value.primaryTeamColor,
      secondaryTeamColor: secondaryTeamColor ?? teamData.value.secondaryTeamColor,
    }
  }
}
window.addEventListener('team-colors-updated', onTeamColorsUpdated)

// === Pull-to-refresh ===
const isRefreshDisabled = computed(() => {
  // Disable na stránkách kde to nedává smysl (login, register, chat, atd.)
  if (route.path.startsWith('/messages')) return true;
  return hideLayout.value;
});

const handleGlobalRefresh = () => {
  // Reload celé stránky - jednoduchý a efektivní způsob
  window.location.reload();
};

// Skrytí celého layoutu na vybraných fixních cestách (přesná shoda je OK)
const hideLayout = computed(() => {
  const hideRoutes = ['/register', '/special-route', '/password', '/passwords', '/cancel-email-change', '/confirm-email-change'];
  return hideRoutes.includes(route.path);
});

// ✅ Správně: schovej user banner (MainMenu) jen na mobilech a jen na routech začínajících na /league nebo /match
const hideUserBanner = computed(() => {
  // Na user profile vždy skryj MainMenu
  if (route.path.startsWith('/users/')) return true;
  // Na event detail vždy skryj MainMenu
  if (route.path.startsWith('/event/')) return true;
  // Na eventdatalog (časomíra) vždy skryj MainMenu
  if (route.path.startsWith('/eventdatalog/')) return true;
  // Na dependants vždy skryj MainMenu (stránka má vlastní QuickActionsMenu)
  if (route.path === '/dependants') return true;
  // Na account stránce skryj MainMenu
  if (route.path === '/account') return true;
  // Na gamecenter skryj MainMenu (má vlastní MainMenuLeague)
  if (route.path.startsWith('/gamecenter/')) return true;
  // Na league sub-routách (/leagues/xxx/...) skryj vždy
  if (route.path.match(/^\/leagues\/.+/)) return true;
  // Na team sub-routách (/teams/xxx/...) skryj vždy
  if (route.path.match(/^\/teams\/.+/)) return true;
  return display.smAndDown.value;                        // ← reaktivní breakpoint
});

// (volitelné) pokud chceš reagovat i při změně breakpointu okamžitě:
watch(
  () => display.smAndDown.value,
  () => { /* případně něco řeš, není nutné */ }
);

// Sledování query (ponecháno)
watch(
  () => route.query,
  (newQuery) => {
    superliga.value = newQuery.superliga || null;
    if (superliga.value) appStore.showPlayRequestOverlay();
  }
);

onMounted(async () => {
  // Přidat scroll listener pro sticky menu
  window.addEventListener('scroll', handleScroll);
  
  try {
    // Počkat na připravenost routeru před dalšími operacemi
    await router.isReady()
    
    if ('serviceWorker' in navigator) {
      // await navigator.serviceWorker.register('/service-worker.js');
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'navigate' && event.data.url) {
          // Zajistit, že router je připraven před navigací
          router.isReady().then(() => {
            router.push(event.data.url).catch((err) => {
              // Ignorovat NavigationDuplicated error
              if (err.name !== 'NavigationDuplicated') {
                console.error('Navigation error:', err)
              }
            })
          })
        }
      });
    }
    userStore.loadUserFromStorage();
    appStore.connect();
  } catch (error) {
    console.error('Chyba při nastavování push notifikací:', error);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('league-colors-updated', onLeagueColorsUpdated);
  window.removeEventListener('team-colors-updated', onTeamColorsUpdated);
});
</script>



<style>
.leaflet-bottom .leaflet-control {
  visibility: hidden;
}

.leaflet-layer,
.leaflet-control-zoom-in,
.leaflet-control-zoom-out,
.leaflet-control-attribution {
  filter: invert(100%) hue-rotate(180deg) brightness(95%) contrast(90%);
}

.v-card.ice-card {
  border-radius: 15px !important;
  background-color: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  /* Pro Safari */
  color: var(--v-mhblue-base);
}

.v-btn.corner-add-button {
  position: fixed !important;
  bottom: 10px !important;
  right: 20px;
  z-index: 1000;
}


html,
body,
#app {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
}

.minimal-select.v-field__input {
  padding: 0 !important;
  margin: 0 !important;
  height: 32px;
  /* Výška inputu */
  line-height: 32px;
  /* Vertikální centrování textu */
}

.vc-week {
  height: 50px;
}

.is-today {
  background-color: rgba(var(--v-theme-sheetText), 0.06);
  border-radius: 5px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.2) !important;
}

.vc-highlight-bg-solid {
  width: 35px;
  height: 35px;
}

.weekday-position-6,
.vc-weekday-7 {
  border-left: 1px solid rgba(var(--v-theme-sheetText), 0.08);
}

.vc-weekday {
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.08);
  background-color: rgba(var(--v-theme-sheetText), 0.03);
}

/* Nastavení maximální šířky obrázků v obsahu */
.article-content img {
  max-width: 100% !important;
  height: auto !important;
  width: auto !important;
  /* Přepíše pevnou šířku nastavenou Quill editorem */
}


.ql-align-center {
  text-align: center;
}

.ql-align-right {
  text-align: right;
}

.ql-align-left {
  text-align: left;
}

ol {
  padding-left: 20px;
  padding-right: 20px;
}

.v-toolbar__content {
  overflow-x: scroll !important;
}


.v-tab {
  text-transform: none !important;
  padding: 0 !important;
}

.v-slide-group__content {
  align-items: center !important;
}

.sticky-menu-container {
  position: relative;
  z-index: 1000;
}

/* Když je menu zafixované nahoře */
.sticky-menu-container.is-fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding-top: env(safe-area-inset-top, 0px);
  background-color: #f3f4f7;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

[data-theme="dark"] .sticky-menu-container.is-fixed {
  background: rgba(0, 0, 0, 0.2);
}

/* Přepsání výchozího pozicování v-app-bar aby nebyl fixed */
.sticky-menu-container .v-app-bar,
.sticky-menu-container .v-toolbar {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
}

.v-application {
  overflow-y: auto !important;
  padding-top: env(safe-area-inset-top, 0px);
}

/* ─── Desktop main-route slide transitions ────────────────────────── */
.rt-view-wrapper {
  position: relative;
  overflow: hidden;
}

.rt-slide-left-enter-active,
.rt-slide-left-leave-active,
.rt-slide-right-enter-active,
.rt-slide-right-leave-active {
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Leaving page goes absolute so it doesn't push the new page down */
.rt-slide-left-leave-active,
.rt-slide-right-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

/* slide-left: new page enters from right */
.rt-slide-left-enter-from  { transform: translateX(60px);  opacity: 0; }
.rt-slide-left-enter-to    { transform: translateX(0);     opacity: 1; }
.rt-slide-left-leave-from  { transform: translateX(0);     opacity: 1; }
.rt-slide-left-leave-to    { transform: translateX(-60px); opacity: 0; }

/* slide-right: new page enters from left */
.rt-slide-right-enter-from { transform: translateX(-60px); opacity: 0; }
.rt-slide-right-enter-to   { transform: translateX(0);     opacity: 1; }
.rt-slide-right-leave-from { transform: translateX(0);     opacity: 1; }
.rt-slide-right-leave-to   { transform: translateX(60px);  opacity: 0; }

/* ─── Mobile bottom fade overlay ──────────────────────────────────── */
.mobile-bottom-fade {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  pointer-events: none;
  z-index: 99;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(var(--v-theme-background), 0.5) 40%,
    rgba(var(--v-theme-background), 0.85) 70%,
    rgb(var(--v-theme-background)) 100%
  );
}

.mobile-top-fade {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  pointer-events: none;
  z-index: 99;
  background: linear-gradient(
    to top,
    transparent 0%,
    rgba(var(--v-theme-background), 0.5) 40%,
    rgba(var(--v-theme-background), 0.85) 70%,
    rgb(var(--v-theme-background)) 100%
  );
}



.ql-snow{
  background-color: transparent !important;
  border: 0px solid rgba(var(--v-theme-sheetText), 0.2) !important;
}

.ql-editor{
  min-height: 550px !important;
  background-color: rgba(var(--v-theme-fourthLineColor), 0.1) !important;
}

</style>
